import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { PROD_BACKEND_BASE_URL } from '../constants';
import { clearStorage } from '../utils';

const AxiosClient: AxiosInstance = axios.create({
  baseURL: PROD_BACKEND_BASE_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

const navigateToLogin = () => {
  clearStorage();
  window.location.href = '/login';
};

AxiosClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = localStorage.getItem('access_token');

    if (config.url?.includes('/login')) {
      return config;
    }

    if (!token) {
      navigateToLogin();
      throw new axios.Cancel('No access token available');
    }

    if (!config.headers?.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => Promise.reject(error)
);

AxiosClient.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  (error: any) => {
    if (error.response) {
      console.error('API Error:', error.response.status, error.response.data);

      if (error.response.status === 401 && error.config.url?.includes('/login')) {
        return Promise.reject(error);
      }

      if (error.response.status === 401) {
        navigateToLogin();
      }
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Request error:', error.message);
    }

    return Promise.reject(error);
  }
);

interface ApiResponse<T = any> {
  data: T;
  status: number;
  message?: string;
}

export const get = async <T = any>(
  url: string,
  params: object = {},
  config: AxiosRequestConfig = {}
): Promise<T> => {
  try {
    const response = await AxiosClient.get<ApiResponse<T>>(url, { params, ...config });
    return response.data as T;
  } catch (error) {
    throw error;
  }
};

export const post = async <T = any>(
  url: string,
  data: object = {},
  config: AxiosRequestConfig = {}
): Promise<T> => {
  try {
    const response = await AxiosClient.post<ApiResponse<T>>(url, data, config);
    return response.data as T;
  } catch (error) {
    throw error;
  }
};

export const put = async <T = any>(
  url: string,
  data: object = {},
  config: AxiosRequestConfig = {}
): Promise<T> => {
  try {
    const response = await AxiosClient.put<ApiResponse<T>>(url, data, config);
    return response.data as T;
  } catch (error) {
    throw error;
  }
};

export const patch = async <T = any>(
  url: string,
  data: object = {},
  config: AxiosRequestConfig = {}
): Promise<T> => {
  try {
    const response = await AxiosClient.patch<ApiResponse<T>>(url, data, config);
    return response.data as T;
  } catch (error) {
    throw error;
  }
};

export default AxiosClient;
